import { useEffect, useCallback, useRef, useState } from 'react';
import { useAppStore } from '../../store';
import {
  getStoredToken,
  isTokenExpired,
  decodeToken,
  useCurrentUser,
  useRefreshToken,
  useLogout,
} from '../../api/auth';

/**
 * AuthProvider — wraps the app to handle:
 * - SSO Mowibo : si l URL contient ?ssoToken=..., on l importe dans localStorage
 *   et on nettoie l URL (history.replaceState). Permet le SSO transparent
 *   depuis Connect/Mowi qui partagent le meme JWT_SECRET.
 * - Auto-restore session on mount (token in localStorage)
 * - Auto-refresh token before expiry
 * - Auto-logout on 401
 */
export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const authLogin = useAppStore((s) => s.authLogin);
  const authLogout = useAppStore((s) => s.authLogout);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [ssoReady, setSsoReady] = useState(false);

  // SSO : import du token via URL param avant tout autre traitement auth.
  // Synchrone : on lit + ecrit localStorage avant que useCurrentUser ne fire.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('ssoToken');
    if (ssoToken && !isTokenExpired(ssoToken)) {
      localStorage.setItem('helios_token', ssoToken);
      // Nettoie l URL : supprime ?ssoToken=... pour eviter qu'il traine
      // dans l historique navigateur ou dans des partages de liens.
      params.delete('ssoToken');
      const qs = params.toString();
      const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
    setSsoReady(true);
  }, []);

  const { data: currentUser } = useCurrentUser();
  const refreshMutation = useRefreshToken();
  useLogout();

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = getStoredToken();
    if (token && !isTokenExpired(token)) {
      // Token exists and is valid — mark as authenticated
      // The useCurrentUser query will fetch user data
      if (currentUser) {
        authLogin(token, currentUser);
      }
    } else if (token && isTokenExpired(token)) {
      // Token expired — clean up
      authLogout();
      localStorage.removeItem('helios_token');
    }
  }, [currentUser, authLogin, authLogout]);

  // Schedule token refresh before expiry
  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const token = getStoredToken();
    if (!token) return;

    const payload = decodeToken(token);
    if (!payload) return;

    // Refresh 2 minutes before expiry
    const expiresIn = payload.exp * 1000 - Date.now();
    const refreshIn = Math.max(expiresIn - 2 * 60_000, 10_000);

    refreshTimerRef.current = setTimeout(() => {
      refreshMutation.mutate();
    }, refreshIn);
  }, [refreshMutation]);

  useEffect(() => {
    if (isAuthenticated) {
      scheduleRefresh();
    }
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isAuthenticated, scheduleRefresh]);

  // Listen for 401 responses (handled in apiClient interceptor, but also clean up store)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'helios_token' && e.newValue === null) {
        authLogout();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [authLogout]);

  // Tant que l import SSO n est pas fait (1 frame), on rend rien pour eviter
  // que useCurrentUser ne fire avec un token absent puis flicker au refetch.
  if (!ssoReady) return null;

  return <>{children}</>;
}
